import {
  Component,
  computed,
  inject,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { form, FormField, maxLength, minLength, required } from '@angular/forms/signals';
import { NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { RedeemWindow } from '../../core/services/redeem-window';
import { MerchantContext } from '../../core/services/merchant-context';
import { finalize, from, mapTo, switchMap } from 'rxjs';
// import { ValidateBin } from '../../core/services/validate-bin';
import { ICheckQuota, RedeemApi } from '../../core/services/redeem-api';
import { IResponse } from '../../core/services/redeem-api';

export interface IRedeem {
  merchantName: string;
  cardNumber: string;
  merchant: MerchantEnum;
}

export enum MerchantEnum {
  JUSTJUICE = 'Just Juice',
  JUSTFRUIT = 'Just Fruit',
  ITIMLAMOON = 'Itim Lamoon',
  MONSOONTEA = 'Monsoon Tea Asiatique The Riverfront',
  THEPORTTHAI = 'The Port Thai Tea & Coffee',
  LAFIORE = 'La Fiore',
  LAFIORECOFFEE = 'La Fiore Coffee & Dessert',
}

export interface IRedeemResponse {
  codeNumber: string;
}
@Component({
  selector: 'app-redeem',
  imports: [FormField, NgClass],
  host: { class: 'flex flex-col shadow-[0px_0px_12px_0px_rgba(0,0,0,0.16)]' },
  template: `
    @if (isRedeemSuccess()) {
      <div class="p-4 h-52 flex items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="50"
            height="50"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
              stroke="var(--color-main-blue)"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9 12L11 14L15 10"
              stroke="var(--color-main-blue)"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <p class="text-main-blue text-xl font-medium text-center">Enjoy your drinks</p>
        </div>
      </div>
    } @else {
      @if (isQuotaChecking()) {
        <div class="p-4 h-80 flex items-center justify-center">
          <div class="flex items-center justify-center">
            <div class="animate-spin rounded-full h-20 w-20 border-b-2 border-main-blue "></div>
          </div>
        </div>
      } @else {
        @if (isQuotaFull()) {
          <div class="p-4 h-80 flex items-center justify-center">
            <div class="flex items-center justify-center flex-col gap-2">
              <span class=" text-gray-500 text-center">{{ fullMessage() }}</span>
            </div>
          </div>
        } @else {
          @if (merchantName) {
            <div class="p-4">
              <form (submit)="onSubmit($event)">
                <div class="flex flex-col gap-2 mt-3">
                  <!-- <label for="cardNo" class="text-sm font-light ">Card no.</label> -->
                  <p class="font-bold">
                    Simply fill in first 9 digits of your Visa card number for validation
                  </p>
                  <div class="w-14">
                    <img src="/card.png" alt="card" class="w-full" />
                  </div>
                  <div class="flex items-center gap-2 text-lg">
                    <input
                      id="cardNo"
                      type="text"
                      placeholder="xxxx - xxxx - x"
                      class="bg-[#F1F1F1] text-lg  px-2 py-1 rounded-md text-center w-1/2"
                      [ngClass]="{
                        'border-2 border-main-red bg-main-red/10':
                          this.cardNoForm.cardNo().touched() && this.cardNoForm.cardNo().invalid(),
                      }"
                      [formField]="cardNoForm.cardNo"
                      (input)="cardInputFormatted($event)"
                    />
                    <p class="font-medium w-1/2">xxx - xxxx</p>
                  </div>
                </div>
                <p class="text-sm text-gray-500 mt-2 flex items-center gap-1 mb-10">
                  <img src="/lock.svg" alt="lock" class="w-4 h-4" />
                  <span>Your card details are encrypted and not stored.</span>
                </p>

                @if (cardNoForm.cardNo().touched() && cardNoForm.cardNo().invalid()) {
                  @for (error of cardNoForm.cardNo().errors(); track error) {
                    <p class="text-sm text-red-500 mt-2 flex items-center gap-1 mb-2">
                      <span>{{ error.message }}</span>
                    </p>
                  }
                }

                @if (errorMessage()) {
                  <p class="text-sm text-red-500 mt-2 flex items-center gap-1 mb-2">
                    <span>{{ errorMessage() }}</span>
                  </p>
                }

                <button
                  type="submit"
                  class="px-4 py-2 w-full  flex items-center justify-center bg-main-blue cursor-pointer"
                  [disabled]="this.isLoading()"
                >
                  @if (this.isLoading()) {
                    <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-white "></div>
                  } @else {
                    <span class="text-white">Verify Card</span>
                  }
                </button>
              </form>
            </div>
          } @else {
            <div class="p-4 h-80 flex items-center justify-center">
              <div class="flex items-center justify-center">
                <p class="text-red-500 text-lg font-medium text-center">
                  Please Scan QR Code at merchant shop to get free drink
                </p>
              </div>
            </div>
          }
        }
      }
    }
  `,
})
export class Redeem implements OnInit, OnChanges {
  @Input() merchantName: MerchantEnum;

  cardNo = signal<{ cardNo: string }>({ cardNo: '' });
  cardNoForm = form(this.cardNo, (f) => {
    required(f.cardNo, { message: 'Please fill the first 9 digits of your card number.' });
    maxLength(f.cardNo, 15, { message: 'Please fill the first 9 digits of your card number.' });
    minLength(f.cardNo, 15, { message: 'Please fill the first 9 digits of your card number.' });
  });

  errorMessage = signal<string>('');

  fullMessage = signal<string>('');

  isLoading = signal(false);
  private router = inject(Router);
  private redeemWindow = inject(RedeemWindow);
  private merchantContext = inject(MerchantContext);
  // private validateBin = inject(ValidateBin);
  private redeemApi = inject(RedeemApi);

  isQuotaFull = signal(false);
  isQuotaChecking = signal(false);

  isRedeemSuccess = signal(false);

  private route = inject(ActivatedRoute);

  ngOnInit() {
    const params = this.route.snapshot.queryParams;
    if (params['redeem'] === 'true') {
      this.isRedeemSuccess.set(true);
    }

    this.checkQuota();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['merchantName']) {
      this.merchantContext.setMerchant(this.merchantName as unknown as string);
    }
  }

  cardInputFormatted(event: Event) {
    this.errorMessage.set('');
    const input = event.target as HTMLInputElement;
    const numericValue = input.value.replace(/\D/g, '').slice(0, 9);
    let formatted = numericValue;

    if (numericValue.length > 8) {
      formatted = numericValue.replace(/(\d{4})(\d{4})(\d{1})/, '$1 - $2 - $3');
    } else if (numericValue.length > 4) {
      formatted = numericValue.replace(/(\d{4})(\d{1,4})/, '$1 - $2');
    }
    input.value = formatted;
    this.cardNo.set({ cardNo: formatted });
  }

  checkQuota() {
    this.isQuotaChecking.set(true);
    this.redeemApi.checkQuota('ASIATIQUE').subscribe({
      next: (res: IResponse<ICheckQuota>) => {
        this.isQuotaFull.set(res.data.isFull);
        if (res.data.reason === 'dailyLimitReached') {
          this.fullMessage.set(
            'Sorry, the daily offer has been fully redeemed, please try again tomorrow.',
          );
        } else {
          this.fullMessage.set('Sorry, the monthly offer has been fully redeemed');
        }
        this.isQuotaChecking.set(false);
      },
      error: (err) => {
        if (err.statusMessage === 'Merchant not found.') {
          this.isQuotaFull.set(true);
          this.errorMessage.set('Sorry, the monthly offer has been fully redeemed');
        }
        this.isQuotaChecking.set(false);
      },
    });
  }
  onSubmit(event: Event) {
    event.preventDefault();
    this.errorMessage.set('');
    if (this.cardNoForm().invalid()) {
      this.cardNoForm().markAsTouched();

      return;
    }

    // const card1 = this.cardNo().cardNo.split('-')[0];
    // const card2 = this.cardNo().cardNo.split('-')[1];
    // const card3 = this.cardNo().cardNo.split('-')[2];
    // const isValid = this.validateBin.checkForeignVisaCard(card1, card2, card3);
    // if (!isValid) {
    //   this.errorMessage.set('Sorry, your Visa card is not eligible for this promotion');
    //   return;
    // }

    this.isLoading.set(true);

    const data: IRedeem = {
      merchantName: 'ASIATIQUE',
      cardNumber: this.cardNo().cardNo,
      merchant: MerchantEnum[this.merchantName],
    };
    // console.log(data);
    // return;
    this.redeemApi
      .redeem(data)
      .pipe(
        switchMap((res: IResponse<IRedeemResponse>) => {
          const r: IRedeemResponse = res.data;

          return from(
            this.redeemWindow.startWindow(r.codeNumber, this.merchantName as unknown as string),
          ).pipe(mapTo(res));
        }),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['/showcode', this.merchantName]);
        },
        error: (err) => {
          if (
            err.statusCode === 400 &&
            err.statusMessage === 'Merchant not found or Code is empty.'
          ) {
            this.isQuotaFull.set(true);
            this.errorMessage.set('Sorry, the monthly offer has been fully redeemed');
          } else {
            this.errorMessage.set(err.statusMessage);
          }
          this.isLoading.set(false);
        },
      });
  }
}
